import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {Timer} from '@gongxigames/framework/core';
import {AbstractReelsView} from '@gongxigames/framework/slot/modules/reels/AbstractReelsView';
import {ReelsModule} from '@gongxigames/framework/slot/modules/reels/ReelsModule';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {ReelsViewEvents} from '@gongxigames/framework/slot/modules/reels/ReelsViewEvents';
import {AnticipatorEvents} from '@gongxigames/framework/slot/modules/anticipator';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels/ReelsEvents';
import {ModuleState} from '@gongxigames/framework/slot/fragments/module/ModuleState';

export class ReelsSequentialStopState extends ModuleState<AbstractReelsView, ReelsModule> {
    protected _currentStoppingReel = 0;
    protected _currentTimeout?: Timer;
    protected _reelStopTime = 0;
    protected _anticipationAnimTimer?: Timer;

    protected setupEvents(): void {
        this.setEventHandler(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onTryinToStopBySpinButton.bind(this));
        this.setEventHandler(ReelsViewEvents.REEL_STOPPED, this.onReelStopped.bind(this));
        this.setEventHandler(ReelsViewEvents.ALL_REELS_STOPPED, this.onAllReelsStopped.bind(this));
        this.setEventHandler(ReelsViewEvents.ALL_REEL_ANIMATIONS_FINISHED, this.onAllReelAnimationsFinished.bind(this));
        this.setEventHandler(AnticipatorEvents.ANIMATION_STARTED, this.onAnticipationAnimationStarted.bind(this));
        this.setEventHandler(WinPresentationEvents.INITIAL_ANIMATION_STARTED, this.onInitialAnimationStarted.bind(this));
    }

    public onEnterState(): void {
        this._currentStoppingReel = -1;
        this._reelStopTime = this.view.reelConstants.getBetweenReelsStoppingTime(this.module.isTurboSpin());
        this.stopNextReel();
    }

    public onLeaveState(): void {
        this._currentTimeout?.stop();
        this._currentTimeout = undefined;
        this._reelStopTime = 0;
    }

    protected onAnticipationAnimationStarted(duration: number): void {
        this._anticipationAnimTimer = this.module.updateManager.addTimer(() => {
            this.onAnticipationAnimationFinished();
        }, duration - this.view.reelConstants.REEL_BOUNCE_TIME, true);
    }

    protected onAnticipationAnimationFinished(): void {
        this.module.removeReelFromAnticipatedList(this.nextReelToStopIndex());
        this.stopNextReel();
    }

    protected stopNextReel(): void {
        this._currentStoppingReel++;

        if (this._currentStoppingReel >= this.view.reelConstants.REELS_AMOUNT) {
            this._currentTimeout && this._currentTimeout.stop();
        } else {
            this.view.tryStoppingReel(this._currentStoppingReel);

            // setting timeout for the next reel to stop only if next reel isn't in the anticipation list
            if (!this.isReelInAnticipationList(this._currentStoppingReel)) {
                this._currentTimeout = this.module.updateManager.addTimer(this.stopNextReel.bind(this), this._reelStopTime, true);
            }
        }
    }

    protected isReelInAnticipationList(reelIndex: number): boolean {
        return this.module.anticipatedReels.indexOf(reelIndex + 1) !== -1;
    }

    protected nextReelToStopIndex(): number {
        return this._currentStoppingReel + 1;
    }

    protected onReelStopped(reelNr: number): void {
        this.emit(ReelsEvents.REEL_STOPPED, reelNr);
    }

    protected onAllReelAnimationsFinished(): void {
        this.module.isAllReelsStopped && this.onSpinEnded();
    }

    protected onAllReelsStopped(): void {
        if (this.module.isBlockingFlow) {
            this.module.isAllAnimationsFinished && this.onSpinEnded();
        } else {
            this.onSpinEnded();
        }
    }

    protected onSpinEnded(): void {
        this.module.gotoState(ReelsModule.BASIC_STATE);
        this.module.eventManager.emit(ReelsEvents.SPIN_ENDED);
    }

    protected onTryinToStopBySpinButton(): void {
        this._anticipationAnimTimer && this._anticipationAnimTimer.stop();
        this.module.anticipatedReels.length = 0;
        this.module.gotoState(ReelsModule.QUICKSTOP_STATE);
    }

    protected onInitialAnimationStarted(pays: Pay[]): void {
        this.view.hidePays(pays);
    }
}
